import React, { useMemo } from 'react';
import { Thumbnail } from '@remotion/player';
import { constructMediaUrl } from '../../../api/helpers';
import { MediaItem } from '../../../api/structs/media';
import {
  GenericCarouselFrame,
  GenericCarouselFrameProps,
  GenericCarouselImage,
} from '../../../remotion/generic-carousel/GenericCarouselFrame';
import { useCarouselPreviewFonts } from '../../../remotion/generic-carousel/useCarouselPreviewFonts';

const thumbnailComponent = GenericCarouselFrame as unknown as React.FC<Record<string, unknown>>;

interface GenericCarouselThumbnailPreviewProps {
  images: MediaItem[];
  activeItemId?: string;
  sceneIndex: number;
  headline: string;
  brandingTag: string;
  draftMetadata?: Record<string, any>;
}

const getOrder = (item: MediaItem): number => Number(item.metadata?.order ?? 9999);

const textFromMetadata = (
  metadata: Record<string, any> | undefined,
  primaryKey: string,
  fallback = ''
): string => {
  const value = metadata?.[primaryKey] ?? metadata?.[primaryKey.toLowerCase()] ?? fallback;
  return String(value ?? '').replace(/\|/g, '\n');
};

const toCarouselImage = (
  image: MediaItem,
  activeItemId: string | undefined,
  draftMetadata: Record<string, any> | undefined,
  imageIndex: number
): GenericCarouselImage => {
  const metadata =
    activeItemId && image.id === activeItemId
      ? { ...image.metadata, ...(draftMetadata || {}) }
      : image.metadata;
  const titleFallback = imageIndex === 0 ? '' : image.prompt;

  return {
    id: image.id,
    url: constructMediaUrl(image.file_url),
    title: textFromMetadata(metadata, 'title', titleFallback),
    explanation: textFromMetadata(metadata, 'explanation'),
  };
};

export const buildGenericCarouselInputProps = ({
  images,
  activeItemId,
  sceneIndex,
  headline,
  brandingTag,
  draftMetadata,
}: GenericCarouselThumbnailPreviewProps): GenericCarouselFrameProps => {
  const orderedImages = [...images]
    .sort((left, right) => getOrder(left) - getOrder(right))
    .map((image, imageIndex) => toCarouselImage(image, activeItemId, draftMetadata, imageIndex));
  const resolvedHeadline = textFromMetadata(draftMetadata, 'headline', headline);

  return {
    variant: sceneIndex === 0 ? 'headline' : 'slide',
    slideIndex: Math.max(0, sceneIndex - 1),
    headline: resolvedHeadline,
    brandingTag: brandingTag || 'weirdphysics',
    stylePresetDraft:
      draftMetadata?.stylePresetDraft && typeof draftMetadata.stylePresetDraft === 'object'
        ? draftMetadata.stylePresetDraft
        : undefined,
    images: orderedImages,
  };
};

const GenericCarouselThumbnailPreview: React.FC<GenericCarouselThumbnailPreviewProps> = ({
  images,
  activeItemId,
  sceneIndex,
  headline,
  brandingTag,
  draftMetadata,
}) => {
  const fontsLoaded = useCarouselPreviewFonts();

  const inputProps: GenericCarouselFrameProps = useMemo(
    () => buildGenericCarouselInputProps({ images, activeItemId, sceneIndex, headline, brandingTag, draftMetadata }),
    [activeItemId, brandingTag, draftMetadata, headline, images, sceneIndex]
  );

  if (!fontsLoaded) {
    return (
      <div className="aspect-[4/5] w-full rounded border border-white/10 bg-zinc-950 flex items-center justify-center text-[10px] uppercase tracking-[0.18em] text-zinc-600 font-mono">
        Loading preview
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded border border-white/10 bg-black">
      <Thumbnail
        component={thumbnailComponent}
        compositionWidth={1080}
        compositionHeight={1350}
        durationInFrames={1}
        fps={30}
        frameToDisplay={0}
        inputProps={inputProps as unknown as Record<string, unknown>}
        style={{
          width: '100%',
          aspectRatio: '4 / 5',
          display: 'block',
        }}
      />
    </div>
  );
};

export default GenericCarouselThumbnailPreview;
